<template>
  <div>
    <!-- === HEADER EN === -->
    <header
      class="executive-header"
      :class="{ 'header-hidden': !headerVisible, 'scrolled': isScrolled }"
    >
      <div class="header-content">
        <!-- Logo -->
        <div class="monogram" @click="handleScrollTo('profile')">
          <img src="@/assets/logo2.jpg" class="monograma" alt="EA Magic" />
        </div>

        <!-- Desktop Nav -->
        <nav class="desktop-nav">
          <ul>
            <li @click="handleScrollTo('about')">About Us</li>
            <li @click="handleScrollTo('services')">Our Services</li>
            <li @click="handleScrollTo('results')">Results</li>
            <li class="buscar" @click="goToSearch" aria-label="Search">
              <svg viewBox="0 0 24 24" class="icono-buscar">
                <path
                  d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 001.57-4.23C15.99 6.01 13.48 3.5 10.49 3.5S5 6.01 5 9s2.51 5.5 5.49 5.5c1.61 0 3.09-.59 4.23-1.57l.27.28v.79L20 20.49 21.49 19 15.5 14zm-5.01 0C8.01 14 6 11.99 6 9.5S8.01 5 10.49 5 15 7.01 15 9.5 12.98 14 10.49 14z"
                />
              </svg>
            </li>
          </ul>
        </nav>

        <!-- Right side (call + CTA) -->
        <div class="header-right">
          <div class="llamada">
            <a class="llamada-texto" @click.prevent href="tel:+16122619287">Call for a Free Quote</a>
            <a class="telefono" href="tel:+16122619287">(612) 261 9287</a>
          </div>
          <a class="boton-contacto" @click.prevent="goToContact">CONTACT US</a>
        </div>

        <!-- Language -->
        <div class="header-controls">
          <div class="language-selector" @click.stop="toggleLang" ref="languageDropdown">
            <span class="current-language">{{ currentLang }}</span>
            <svg class="dropdown-icon" viewBox="0 0 24 24"><path d="M7 10l5 5 5-5z"/></svg>
            <transition name="fade-dropdown">
              <ul v-if="showLangs" class="idioma-dropdown">
                <li @click="switchLang('English')" :class="{ active: currentLang === 'English' }">
                  <img src="@/assets/us.png" alt="US" class="flag-icon" />
                  <span>English</span>
                </li>
                <li @click="switchLang('Español')" :class="{ active: currentLang === 'Español' }">
                  <img src="@/assets/mx.png" alt="ES" class="flag-icon" />
                  <span>Español</span>
                </li>
              </ul>
            </transition>
          </div>
        </div>

        <!-- Mobile burger -->
        <button class="hamburger" :class="{ active: mobileMenuOpen }" @click="toggleMobileMenu">
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
      </div>

      <!-- Mobile Nav -->
      <transition name="slide-menu">
        <nav class="mobile-nav" v-if="mobileMenuOpen">
          <ul>
            <li @click="handleScrollTo('about')">About Us</li>
            <li @click="handleScrollTo('services')">Our Services</li>
            <li @click="handleScrollTo('results')">Results</li>
            <li @click="goToContact">Contact Us</li>
          </ul>
          <div class="mobile-language-selector">
            <button @click="switchLang('English')" :class="{ active: currentLang === 'English' }">
              <img src="@/assets/us.png" alt="US" class="flag-icon" />
            </button>
            <button @click="switchLang('Español')" :class="{ active: currentLang === 'Español' }">
              <img src="@/assets/mx.png" alt="ES" class="flag-icon" />
            </button>
          </div>
        </nav>
      </transition>
    </header>

    <!-- === PAGE CONTENT (EN) === -->
    <router-view />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'LayoutEN',
  setup() {
    const router = useRouter()
    const route = useRoute()

    const currentLang = ref(
      route.path.startsWith('/en') || localStorage.getItem('lang') === 'en'
        ? 'English'
        : 'Español'
    )
    const showLangs = ref(false)
    const languageDropdown = ref(null)
    const mobileMenuOpen = ref(false)
    const headerVisible = ref(true)
    const lastScrollPosition = ref(0)
    const isScrolled = ref(false)

    // Navigation
    const goToContact = () => { router.push('/en/contact'); mobileMenuOpen.value = false }
    const goToSearch  = () => { router.push('/en/search') }

    // Language switch
    const switchLang = (lang) => {
      if (lang === 'Español') {
        currentLang.value = 'Español'
        localStorage.setItem('lang', 'es')
        showLangs.value = false
        mobileMenuOpen.value = false
        if (!route.path.startsWith('/')) router.push('/')
        else router.push('/')
        return
      }
      currentLang.value = 'English'
      localStorage.setItem('lang', 'en')
      showLangs.value = false
      mobileMenuOpen.value = false
      if (!route.path.startsWith('/en')) router.push('/en')
    }

    const toggleLang = () => { showLangs.value = !showLangs.value }
    const toggleMobileMenu = () => { mobileMenuOpen.value = !mobileMenuOpen.value }

    // Hide/show header on scroll
    const handleScroll = () => {
      const y = window.pageYOffset || document.documentElement.scrollTop
      if (y > 100) {
        isScrolled.value = true
        if (Math.abs(y - lastScrollPosition.value) < 10) return
        headerVisible.value = y < lastScrollPosition.value
      } else {
        isScrolled.value = false
        headerVisible.value = true
      }
      lastScrollPosition.value = y
    }

    const handleClickOutside = (e) => {
      if (languageDropdown.value && !languageDropdown.value.contains(e.target)) {
        showLangs.value = false
      }
    }

    // Scroll to EN sections
    const handleScrollTo = (idEN) => {
      if (route.path === '/en') {
        window.dispatchEvent(new CustomEvent('scroll-to-section', { detail: { id: idEN } }))
      } else {
        localStorage.setItem('scrollTarget', idEN)
        router.push('/en')
      }
      mobileMenuOpen.value = false
    }

    onMounted(async () => {
      document.addEventListener('click', handleClickOutside)
      window.addEventListener('scroll', handleScroll)

      // If we came from ES with a target saved, map ES -> EN and scroll
      const target = localStorage.getItem('scrollTarget')
      if (target) {
        localStorage.removeItem('scrollTarget')
        await nextTick()
        const mapEStoEN = { perfil: 'profile', logros: 'about', servicios: 'services', trayectoria: 'results' }
        const id = mapEStoEN[target] || target
        window.dispatchEvent(new CustomEvent('scroll-to-section', { detail: { id } }))
      }
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      currentLang, showLangs, languageDropdown,
      mobileMenuOpen, headerVisible, isScrolled,
      toggleLang, switchLang, toggleMobileMenu,
      goToContact, goToSearch, handleScrollTo
    }
  }
}
</script>

<style scoped>
/* Same styling as ES */
.executive-header{
  position: fixed; top:0; left:0; right:0;
  background:#ffffff; backdrop-filter: blur(10px);
  z-index:1000; transition: all .35s ease;
  box-shadow: 0 2px 12px rgba(0,0,0,.06);
  border-bottom: 1px solid rgba(0,0,0,.04);
  transform: translateY(0);
}
.header-hidden{ transform: translateY(-100%); }
.scrolled{ box-shadow: 0 4px 24px rgba(0,0,0,.1); }
.executive-header::before{ content:''; position:absolute; top:0; left:0; right:0; height:4px; background:#e8f3f9; }

.header-content{ margin-top:10px; max-width:1400px; margin:0 auto; padding:0 24px;
  height:110px; display:flex; align-items:center; justify-content:space-between; }
.monogram{ display:flex; align-items:center; height:100%; cursor:pointer; }
.monograma{ height:124px; width:auto; object-fit:contain; display:block; }

.desktop-nav ul{ display:flex; gap:28px; list-style:none; margin:0 18px 0 24px; padding:0; align-items:center; }
.desktop-nav li{ font-size:.98rem; font-weight:700; color:#111827; cursor:pointer; position:relative; padding:10px 0; letter-spacing:.2px; }
.desktop-nav li:hover{ color:#1f2937; }
.desktop-nav li::after{ content:''; position:absolute; bottom:0; left:0; width:0; height:2px; background:#9abf3b; transition:width .25s ease; }
.desktop-nav li:hover::after{ width:100%; }

.buscar{ padding-left:6px; }
.icono-buscar{ width:22px; height:22px; fill:#6aa13b; display:block; }

.header-right{ display:flex; align-items:center; gap:18px; }
.llamada{ display:flex; flex-direction:column; align-items:flex-end; line-height:1.1; }
.llamada-texto{ color:#1e73be; font-weight:700; font-size:.92rem; text-decoration:none; }
.llamada-texto:hover{ text-decoration:underline; }
.telefono{ color:#111827; font-weight:800; font-size:1.12rem; letter-spacing:.5px; text-decoration:none; }
.boton-contacto{ background:#5d9c3a; color:#fff; padding:12px 16px; font-weight:700; text-decoration:none; border-radius:0;
  display:inline-flex; align-items:center; justify-content:center; min-width:132px; }
.boton-contacto:hover{ filter:brightness(.95); }

.header-controls{ display:flex; align-items:center; gap:18px; }
.language-selector{ position:relative; cursor:pointer; display:flex; align-items:center; gap:6px; }
.current-language{ font-weight:500; font-size:.95rem; color:#4a5568; }
.dropdown-icon{ width:16px; height:16px; fill:#7f8c8d; }
.idioma-dropdown{ position:absolute; top:calc(100% + 10px); right:0; background:#fff; border-radius:8px;
  box-shadow:0 10px 25px rgba(0,0,0,.12); padding:8px 0; min-width:140px; z-index:1001; }
.idioma-dropdown li{ padding:10px 16px; font-size:.95rem; color:#4a5568; display:flex; align-items:center; gap:8px; }
.idioma-dropdown li:hover{ background:#f8fafc; color:#768d9c; }
.idioma-dropdown li.active{ background:#f0f7ff; }
.flag-icon{ width:18px; height:12px; border-radius:2px; object-fit:cover; box-shadow:0 0 2px rgba(0,0,0,.1); }

.hamburger{ display:none; flex-direction:column; justify-content:center; align-items:center; width:40px; height:40px; background:transparent; border:none; cursor:pointer; padding:0; z-index:1002; }
.hamburger-line{ width:24px; height:2px; background:#2c3e50; margin:4px 0; transition:all .3s ease; transform-origin:center; }
.hamburger.active .hamburger-line:nth-child(1){ transform: translateY(6px) rotate(45deg); }
.hamburger.active .hamburger-line:nth-child(2){ opacity:0; }
.hamburger.active .hamburger-line:nth-child(3){ transform: translateY(-6px) rotate(-45deg); }

.mobile-nav{ background:#ffffff !important; color:#272626 !important; }
.mobile-nav ul{ gap:24px; list-style:none; padding:0; margin:0; }
.mobile-nav li{ font-size:1.1rem; font-weight:600; color:#2e3032; cursor:pointer; padding:12px 0; border-bottom:1px solid rgba(0,0,0,.06); }
.mobile-nav li:hover{ color:#3c4043; }

.mobile-language-selector{ display:flex; gap:15px; margin-top:auto; }
.mobile-language-selector button{ background:none; border:1px solid #e2e8f0; padding:8px 20px; border-radius:20px; font-weight:500; color:#4a5568; }
.mobile-language-selector button.active{ background:#75797c; color:#fff; border-color:#3498db; }

.fade-dropdown-enter-active,.fade-dropdown-leave-active{ transition:all .2s ease; }
.fade-dropdown-enter-from,.fade-dropdown-leave-to{ opacity:0; transform:translateY(-10px); }
.slide-menu-enter-active,.slide-menu-leave-active{ transition:all .4s cubic-bezier(.65,0,.35,1); }
.slide-menu-enter-from,.slide-menu-leave-to{ transform:translateY(-100%); opacity:0; }

@media (max-width: 1200px){ .desktop-nav ul{ gap:22px; } .header-right{ gap:12px; }
}
@media (max-width: 1024px){
  .desktop-nav{ display:none; }
  .hamburger{ display:flex; }
  .header-content{ padding:0 16px; height:104px; }
  .language-selector{ display:none !important; }
  .header-right{ display:none; }
}
</style>
